import { useMemo, useState } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import CleanupConfirmDialog from './CleanupConfirmDialog';

export default function CleanupActionButtons({ actions = [], direction = 'column' }) {
  const [activeActionKey, setActiveActionKey] = useState(null);
  const [phraseValue, setPhraseValue] = useState('');

  const activeAction = useMemo(
    () => actions.find(action => action.key === activeActionKey),
    [actions, activeActionKey]
  );

  const phraseConfig = activeAction?.confirm?.requirePhrase || null;

  const normalizedPhrase = phraseConfig?.phrase ? phraseConfig.phrase.trim().toUpperCase() : null;
  const currentPhrase = phraseValue.trim().toUpperCase();
  const phraseMatches = !phraseConfig || currentPhrase === normalizedPhrase;

  const handleExecute = async action => {
    if (!action || typeof action.onExecute !== 'function') {
      return;
    }
    await action.onExecute();
  };

  const handleButtonClick = action => {
    if (!action || action.disabled) {
      return;
    }
    if (action.confirm) {
      setActiveActionKey(action.key);
      setPhraseValue('');
    } else {
      handleExecute(action);
    }
  };

  const handleCloseDialog = () => {
    setActiveActionKey(null);
    setPhraseValue('');
  };

  const handleConfirm = async () => {
    if (!activeAction) {
      return;
    }
    await handleExecute(activeAction);
    handleCloseDialog();
  };

  const confirmContent = useMemo(() => {
    if (!activeAction?.confirm) {
      return null;
    }
    if (typeof activeAction.confirm.content === 'function') {
      return activeAction.confirm.content({ phraseValue, setPhraseValue, phraseMatches });
    }
    const blocks = [];
    if (activeAction.confirm.description) {
      blocks.push(
        <Typography key='description'>{activeAction.confirm.description}</Typography>
      );
    }
    if (phraseConfig) {
      blocks.push(
        <Typography key='prompt' sx={{ mb: 2 }}>
          {phraseConfig.prompt || 'Bitte schreibe den folgenden Satz, um die Aktion zu bestätigen:'}
        </Typography>
      );
      blocks.push(
        <Typography key='phrase' sx={{ fontWeight: 600, mb: 2 }}>
          {phraseConfig.phrase}
        </Typography>
      );
      blocks.push(
        <TextField
          key='input'
          fullWidth
          autoFocus
          label={phraseConfig.label || 'Bestätigung'}
          value={phraseValue}
          onChange={event => setPhraseValue(event.target.value)}
          error={phraseValue.length > 0 && !phraseMatches}
          helperText={phraseConfig.helperText}
        />
      );
    }
    return blocks;
  }, [activeAction, phraseConfig, phraseValue, phraseMatches]);

  const confirmDisabled =
    activeAction?.loading ||
    activeAction?.disabled ||
    (phraseConfig ? !phraseMatches || phraseValue.length === 0 : false);

  return (
    <>
      <Stack direction={direction} spacing={2} alignItems='stretch'>
        {actions.map(action => (
          <Button
            key={action.key}
            variant={action.variant || 'contained'}
            color={action.color || 'error'}
            onClick={() => handleButtonClick(action)}
            disabled={action.disabled}
            fullWidth={action.fullWidth !== false}
          >
            {action.label}
          </Button>
        ))}
      </Stack>

      <CleanupConfirmDialog
        open={Boolean(activeAction)}
        onClose={handleCloseDialog}
        onConfirm={handleConfirm}
        confirmDisabled={confirmDisabled}
        confirmLabel={activeAction?.confirm?.confirmLabel || 'Bestätigen'}
        title={activeAction?.confirm?.title || 'Bestätigen'}
        content={confirmContent}
        description={
          !activeAction?.confirm?.content && !confirmContent
            ? activeAction?.confirm?.description || null
            : null
        }
      />
    </>
  );
}

